/**
 * AI 상담 Vuex 모듈
 */

import { invokeBedrockStream, parseJsonFromResponse } from '@/services/BedrockService'
import { calculateEstimate, MODULE_CATALOG } from '@/constants/modulesPricing'
import {
  IDEA_ELABORATION_SYSTEM_PROMPT,
  PROPOSAL_GENERATION_SYSTEM_PROMPT,
  createIdeaMessage,
  createProposalMessage
} from '@/constants/prompts'

const initialState = () => ({
  // 단계 관리
  currentStep: 1,
  maxReachedStep: 1,

  // Step 1: 아이디어 입력
  ideaInput: {
    prompt: '',
    platformType: '',
    projectName: ''
  },

  // Step 2: AI 분석 결과
  aiElaboration: {
    rawResponse: '',
    isLoading: false,
    isStreaming: false,
    error: null,
    parsedResult: null
  },

  // Step 3: 모듈 선택
  selectedModules: [],

  // Step 4: 견적
  estimate: null,

  // Step 5: 기획서
  proposal: {
    content: '',
    isLoading: false,
    error: null
  },

  // Step 6: 연락처
  contactInfo: {
    consultingType: 'P',
    name: '',
    phone: '',
    email: '',
    company: ''
  },

  // 제출 상태
  isSubmitting: false,
  submitError: null,
  submitSuccess: false
})

const state = initialState()

const getters = {
  // 현재 단계
  currentStep: state => state.currentStep,

  // 아이디어 입력 완료 여부
  isIdeaComplete: state => {
    return state.ideaInput.prompt.trim().length > 10 && state.ideaInput.platformType
  },

  // AI 분석 결과
  elaborationResult: state => state.aiElaboration.parsedResult,

  // AI 추천 모듈 목록
  suggestedModules: state => {
    if (!state.aiElaboration.parsedResult?.suggestedModules) return []
    return state.aiElaboration.parsedResult.suggestedModules.map(sm => ({
      ...MODULE_CATALOG[sm.moduleId],
      reason: sm.reason,
      selected: state.selectedModules.includes(sm.moduleId)
    })).filter(m => m.id)
  },

  // 전체 모듈 목록 (선택 상태 포함)
  allModulesWithSelection: state => {
    return Object.values(MODULE_CATALOG).map(module => ({
      ...module,
      selected: state.selectedModules.includes(module.id)
    }))
  },

  // 선택된 모듈 상세 정보
  selectedModulesDetails: state => {
    return state.selectedModules.map(id => MODULE_CATALOG[id]).filter(Boolean)
  },

  // 견적 정보
  estimateInfo: state => state.estimate,

  // 연락처 입력 완료 여부
  isContactComplete: state => {
    const { name, phone, email } = state.contactInfo
    return name.trim() && phone.trim() && email.trim()
  }
}

const mutations = {
  // 단계 설정
  SET_STEP(state, step) {
    state.currentStep = step
    if (step > state.maxReachedStep) {
      state.maxReachedStep = step
    }
  },

  // 아이디어 입력 업데이트
  UPDATE_IDEA_INPUT(state, payload) {
    state.ideaInput = { ...state.ideaInput, ...payload }
  },

  // AI 분석 상태 업데이트
  SET_ELABORATION_LOADING(state, isLoading) {
    state.aiElaboration.isLoading = isLoading
    if (isLoading) {
      state.aiElaboration.error = null
    }
  },

  SET_ELABORATION_STREAMING(state, isStreaming) {
    state.aiElaboration.isStreaming = isStreaming
  },

  SET_ELABORATION_RESPONSE(state, response) {
    state.aiElaboration.rawResponse = response
  },

  APPEND_ELABORATION_RESPONSE(state, chunk) {
    state.aiElaboration.rawResponse += chunk
  },

  SET_ELABORATION_RESULT(state, result) {
    state.aiElaboration.parsedResult = result
  },

  SET_ELABORATION_ERROR(state, error) {
    state.aiElaboration.error = error
    state.aiElaboration.isLoading = false
    state.aiElaboration.isStreaming = false
  },

  // 모듈 선택/해제
  TOGGLE_MODULE(state, moduleId) {
    const index = state.selectedModules.indexOf(moduleId)
    if (index === -1) {
      state.selectedModules.push(moduleId)
    } else {
      state.selectedModules.splice(index, 1)
    }
  },

  SET_SELECTED_MODULES(state, modules) {
    state.selectedModules = modules
  },

  // 견적 설정
  SET_ESTIMATE(state, estimate) {
    state.estimate = estimate
  },

  // 기획서 업데이트
  SET_PROPOSAL_LOADING(state, isLoading) {
    state.proposal.isLoading = isLoading
    if (isLoading) {
      state.proposal.error = null
    }
  },

  SET_PROPOSAL_CONTENT(state, content) {
    state.proposal.content = content
  },

  APPEND_PROPOSAL_CONTENT(state, chunk) {
    state.proposal.content += chunk
  },

  SET_PROPOSAL_ERROR(state, error) {
    state.proposal.error = error
    state.proposal.isLoading = false
  },

  // 연락처 업데이트
  UPDATE_CONTACT_INFO(state, payload) {
    state.contactInfo = { ...state.contactInfo, ...payload }
  },

  // 제출 상태
  SET_SUBMITTING(state, isSubmitting) {
    state.isSubmitting = isSubmitting
  },

  SET_SUBMIT_ERROR(state, error) {
    state.submitError = error
  },

  SET_SUBMIT_SUCCESS(state, success) {
    state.submitSuccess = success
  },

  // 초기화
  RESET_STATE(state) {
    Object.assign(state, initialState())
  }
}

const actions = {
  // 다음 단계로 이동
  nextStep({ commit, state }) {
    if (state.currentStep < 6) {
      commit('SET_STEP', state.currentStep + 1)
    }
  },

  // 이전 단계로 이동
  prevStep({ commit, state }) {
    if (state.currentStep > 1) {
      commit('SET_STEP', state.currentStep - 1)
    }
  },

  // 특정 단계로 이동
  goToStep({ commit, state }, step) {
    if (step >= 1 && step <= state.maxReachedStep) {
      commit('SET_STEP', step)
    }
  },

  // 아이디어 입력 업데이트
  updateIdeaInput({ commit }, payload) {
    commit('UPDATE_IDEA_INPUT', payload)
  },

  // AI 아이디어 분석 실행
  async elaborateIdea({ commit, state }) {
    commit('SET_ELABORATION_LOADING', true)
    commit('SET_ELABORATION_STREAMING', true)
    commit('SET_ELABORATION_RESPONSE', '')

    try {
      const userMessage = createIdeaMessage(state.ideaInput)

      const response = await invokeBedrockStream(
        [{ role: 'user', content: userMessage }],
        {
          systemPrompt: IDEA_ELABORATION_SYSTEM_PROMPT,
          maxTokens: 2048,
          temperature: 0.7,
          onChunk: (chunk) => {
            commit('APPEND_ELABORATION_RESPONSE', chunk)
          }
        }
      )

      commit('SET_ELABORATION_STREAMING', false)
      commit('SET_ELABORATION_LOADING', false)

      // JSON 파싱
      const parsed = parseJsonFromResponse(response.content)
      if (parsed) {
        commit('SET_ELABORATION_RESULT', parsed)

        // AI 추천 모듈 자동 선택
        if (parsed.suggestedModules) {
          const moduleIds = parsed.suggestedModules
            .map(sm => sm.moduleId)
            .filter(id => MODULE_CATALOG[id])
          commit('SET_SELECTED_MODULES', moduleIds)
        }
      } else {
        commit('SET_ELABORATION_ERROR', 'AI 응답을 파싱할 수 없습니다.')
      }

      return response
    } catch (error) {
      commit('SET_ELABORATION_ERROR', error.message)
      throw error
    }
  },

  // 모듈 선택 토글
  toggleModule({ commit }, moduleId) {
    commit('TOGGLE_MODULE', moduleId)
  },

  // 견적 계산
  calculateEstimate({ commit, state }) {
    const estimate = calculateEstimate(
      state.selectedModules,
      state.ideaInput.platformType,
      state.aiElaboration.parsedResult?.estimatedComplexity || 'medium'
    )
    commit('SET_ESTIMATE', estimate)
    return estimate
  },

  // 기획서 생성
  async generateProposal({ commit, state }) {
    commit('SET_PROPOSAL_LOADING', true)
    commit('SET_PROPOSAL_CONTENT', '')

    try {
      const data = {
        problemStatement: state.aiElaboration.parsedResult?.problemStatement || '',
        targetUsers: state.aiElaboration.parsedResult?.targetUsers || '',
        coreFeatures: state.aiElaboration.parsedResult?.coreFeatures || [],
        optionalFeatures: state.aiElaboration.parsedResult?.optionalFeatures || [],
        selectedModules: state.selectedModules,
        platformType: state.ideaInput.platformType,
        estimate: state.estimate
      }

      const userMessage = createProposalMessage(data)

      const response = await invokeBedrockStream(
        [{ role: 'user', content: userMessage }],
        {
          systemPrompt: PROPOSAL_GENERATION_SYSTEM_PROMPT,
          maxTokens: 2048,
          temperature: 0.5,
          onChunk: (chunk) => {
            commit('APPEND_PROPOSAL_CONTENT', chunk)
          }
        }
      )

      commit('SET_PROPOSAL_LOADING', false)
      return response
    } catch (error) {
      commit('SET_PROPOSAL_ERROR', error.message)
      throw error
    }
  },

  // 연락처 업데이트
  updateContactInfo({ commit }, payload) {
    commit('UPDATE_CONTACT_INFO', payload)
  },

  // 상담 신청 제출
  async submitConsultation({ commit, state }) {
    commit('SET_SUBMITTING', true)
    commit('SET_SUBMIT_ERROR', null)

    try {
      // 제출할 데이터 구성
      const submitData = {
        ideaInput: state.ideaInput,
        elaboration: state.aiElaboration.parsedResult,
        selectedModules: state.selectedModules,
        estimate: state.estimate,
        proposal: state.proposal.content,
        contactInfo: state.contactInfo
      }

      // TODO: 실제 API 제출 로직 구현
      console.log('Submit data:', submitData)

      // 성공 시뮬레이션 (실제 구현 시 API 호출로 대체)
      await new Promise(resolve => setTimeout(resolve, 1000))

      commit('SET_SUBMIT_SUCCESS', true)
      commit('SET_SUBMITTING', false)

      return submitData
    } catch (error) {
      commit('SET_SUBMIT_ERROR', error.message)
      commit('SET_SUBMITTING', false)
      throw error
    }
  },

  // 상태 초기화
  resetState({ commit }) {
    commit('RESET_STATE')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
